import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SystemFieldsPanel } from './SystemFieldsPanel';
import { getProfile, saveProfile, getExpertiseTags } from '../../services/profileService';
import { trackAction } from '../../services/gamificationService';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

function TagInput({ tags, onAdd, onRemove, placeholder, colorClass = 'bg-primary-50 text-primary-700 hover:bg-primary-100' }) {
  const [input, setInput] = useState('');

  const commit = () => {
    const trimmed = input.trim();
    if (trimmed && !tags.includes(trimmed)) onAdd(trimmed);
    setInput('');
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
    if (e.key === 'Backspace' && !input && tags.length) onRemove(tags[tags.length - 1]);
  };

  return (
    <div className="flex flex-wrap gap-1.5 p-2 border border-slate-200 dark:border-zinc-700 dark:bg-zinc-800/40 rounded-xl focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent min-h-[44px]">
      {tags.map((tag) => (
        <span key={tag} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${colorClass}`}>
          {tag}
          <button type="button" onClick={() => onRemove(tag)} className="opacity-60 hover:opacity-100">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </span>
      ))}
      <input
        type="text"
        value={input}
        onChange={(e) => {
          const v = e.target.value;
          if (v.endsWith(',')) { const t = v.slice(0, -1).trim(); if (t && !tags.includes(t)) onAdd(t); setInput(''); }
          else setInput(v);
        }}
        onKeyDown={handleKey}
        onBlur={commit}
        placeholder={tags.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[120px] outline-none text-sm bg-transparent text-slate-700 dark:text-zinc-200 placeholder-slate-400 dark:placeholder-zinc-500"
      />
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${checked ? 'bg-teal-500' : 'bg-slate-200 dark:bg-zinc-600'}`}
      role="switch"
      aria-checked={checked}
      aria-label={label}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  );
}

export function EditProfileModal({ employee, onClose, onSave, manager = null, isAdmin = false }) {
  const [activeTab, setActiveTab] = useState('media');
  const [isSaving, setIsSaving] = useState(false);
  const bannerInputRef = useRef(null);

  // Media
  const [banner, setBanner] = useState(null);
  // Brand ramp, not indigo/violet/cyan — these are the colours a profile gets by
  // default, so they set the tone of the whole app.
  const [gradientColor1, setGradientColor1] = useState('#0F766E');
  const [gradientColor2, setGradientColor2] = useState('#0D9488');
  const [gradientColor3, setGradientColor3] = useState('#7DD3C0');
  const [gradientAngle, setGradientAngle] = useState(135);

  // About
  const [bio, setBio] = useState('');
  const [coreExpertise, setCoreExpertise] = useState('');
  const [askMeAbout, setAskMeAbout] = useState('');

  // Expertise
  const [areasOfExpertise, setAreasOfExpertise] = useState([]);
  const [expertiseCategories, setExpertiseCategories] = useState([]);
  const [skills, setSkills] = useState([]);
  const [programsExpertise, setProgramsExpertise] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [newCert, setNewCert] = useState({ name: '', issuer: '', date: '', expires: '' });

  // Background
  const [practiceArea, setPracticeArea] = useState('');
  const [statesLicensed, setStatesLicensed] = useState([]);
  const [highSchoolAlmaMater, setHighSchoolAlmaMater] = useState('');
  const [collegeAlmaMater, setCollegeAlmaMater] = useState('');
  const [volunteerAt, setVolunteerAt] = useState('');
  const [interests, setInterests] = useState([]);

  // Projects
  const [moregroupProjects, setMoregroupProjects] = useState([]);
  const [newProject, setNewProject] = useState({ name: '', role: '', year: '' });
  const [priorProjects, setPriorProjects] = useState('');

  // Open To
  const [openToSpeaking, setOpenToSpeaking] = useState(false);
  const [openToWhitePaper, setOpenToWhitePaper] = useState(false);
  const [openToMentoring, setOpenToMentoring] = useState(false);
  const [mentorTopics, setMentorTopics] = useState('');
  const [innovationTopics, setInnovationTopics] = useState('');

  useEffect(() => {
    if (!employee) return;
    const p = getProfile(employee.id);
    setBanner(p.banner || null);
    setBio(p.bio || '');
    setCoreExpertise(p.coreExpertise || '');
    setAskMeAbout(p.askMeAbout || '');
    setAreasOfExpertise(p.areasOfExpertise || []);
    setSkills(p.skills || []);
    setProgramsExpertise(p.programsExpertise || []);
    setCertifications(p.certifications || []);
    setPracticeArea(p.practiceArea || '');
    setStatesLicensed(p.statesLicensed || []);
    setHighSchoolAlmaMater(p.highSchoolAlmaMater || '');
    setCollegeAlmaMater(p.collegeAlmaMater || '');
    setVolunteerAt(p.volunteerAt || '');
    setInterests(p.interests || []);
    setMoregroupProjects(p.moregroupProjects || []);
    setPriorProjects(p.priorProjects || '');
    setOpenToSpeaking(p.openToSpeaking || false);
    setOpenToWhitePaper(p.openToWhitePaper || false);
    setOpenToMentoring(p.openToMentoring || false);
    setMentorTopics(p.mentorTopics || '');
    setInnovationTopics(p.innovationTopics || '');
    setExpertiseCategories(getExpertiseTags());
  }, [employee]);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleBannerChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Banner must be less than 5MB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => setBanner(reader.result);
    reader.readAsDataURL(file);
  };

  const toggleExpertiseTag = (tag) => {
    setAreasOfExpertise((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const addProject = () => {
    if (!newProject.name.trim()) return;
    setMoregroupProjects((prev) => [...prev, { id: Date.now().toString(), ...newProject }]);
    setNewProject({ name: '', role: '', year: '' });
  };

  const removeProject = (id) => setMoregroupProjects((prev) => prev.filter((p) => p.id !== id));

  const addCertification = () => {
    if (!newCert.name.trim()) return;
    setCertifications((prev) => [...prev, { id: Date.now().toString(), ...newCert }]);
    setNewCert({ name: '', issuer: '', date: '', expires: '' });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const profileData = {
        photo: null, banner, bio, coreExpertise, askMeAbout,
        areasOfExpertise, skills, programsExpertise, certifications,
        practiceArea, statesLicensed, highSchoolAlmaMater, collegeAlmaMater,
        volunteerAt, interests, moregroupProjects, priorProjects,
        openToSpeaking, openToWhitePaper, openToMentoring, mentorTopics,
        innovationTopics,
      };
      saveProfile(employee.id, profileData);

      // Gamification: credit profile-building actions. trackAction only
      // awards each badge once, so re-saves are harmless.
      const expertiseCount = new Set(
        [...skills, ...areasOfExpertise, ...programsExpertise]
          .map((item) => String(item || '').trim())
          .filter(Boolean),
      ).size;
      if (banner) trackAction(employee.id, 'custom_banner', { profile: profileData });
      if (bio?.trim()) trackAction(employee.id, 'add_bio', { profile: profileData });
      if (expertiseCount > 0) {
        trackAction(employee.id, 'add_skills', { expertiseCount, profile: profileData });
      }

      onSave?.(profileData);
      onClose();
    } catch (err) {
      console.error('Failed to save profile:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (!employee) return null;

  const tabs = [
    { id: 'media', label: 'Media' },
    { id: 'about', label: 'About' },
    { id: 'expertise', label: 'Expertise' },
    { id: 'background', label: 'Background' },
    { id: 'projects', label: 'Projects' },
    { id: 'opento', label: 'Open To' },
    { id: 'system', label: 'Locked fields' },
  ];

  const fieldLabel = (text, hint) => (
    <div className="mb-1.5">
      <span className="text-sm font-medium text-slate-700 dark:text-zinc-300">{text}</span>
      {hint && <span className="ml-2 text-xs text-slate-400 dark:text-zinc-500">{hint}</span>}
    </div>
  );

  const inputBase = 'w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-slate-800 dark:text-zinc-200 placeholder-slate-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm';
  const textareaBase = 'w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-slate-800 dark:text-zinc-200 placeholder-slate-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-sm';
  const smallInputBase = 'w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-slate-800 dark:text-zinc-200 placeholder-slate-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm';

  const textInput = (value, onChange, placeholder) => (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={inputBase}
    />
  );

  const textArea = (value, onChange, placeholder, rows = 4) => (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className={textareaBase}
    />
  );

  const bannerPreviewStyle = (() => {
    if (!banner) return { background: 'linear-gradient(135deg, #0F766E, #0D9488, #7DD3C0)' };
    if (banner.startsWith('custom:')) {
      const parts = banner.split(':');
      return { background: `linear-gradient(${parts[1] || 135}deg, ${parts[2] || '#0F766E'}, ${parts[3] || '#0D9488'}, ${parts[4] || '#7DD3C0'})` };
    }
    if (banner.startsWith('gradient:')) return undefined;
    return { backgroundImage: `url(${banner})`, backgroundSize: 'cover', backgroundPosition: 'center' };
  })();

  const bannerClassName = banner?.startsWith('gradient:') ? `bg-gradient-to-br ${banner.replace('gradient:', '')}` : '';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl max-w-2xl w-full h-[min(860px,90vh)] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 dark:border-zinc-700 flex items-center justify-between flex-shrink-0">
            <div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-zinc-100">Edit Profile</h2>
              <p className="text-sm text-slate-500 dark:text-zinc-400">{employee.displayName}</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors">
              <svg className="w-5 h-5 text-slate-500 dark:text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="px-6 pt-3 pb-0 border-b border-slate-100 dark:border-zinc-800 flex-shrink-0 overflow-x-auto">
            <div className="flex gap-0.5 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium transition-colors relative whitespace-nowrap
                    ${activeTab === tab.id
                      ? 'text-primary-700 dark:text-primary-400'
                      : 'text-slate-500 dark:text-zinc-400 hover:text-slate-700 dark:hover:text-zinc-200'}`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5">
            {activeTab === 'system' && (
              <SystemFieldsPanel
                employee={employee}
                manager={manager}
                isAdmin={isAdmin}
              />
            )}

            {/* ── MEDIA ─────────────────────────────────────────── */}
            {activeTab === 'media' && (
              <>
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-slate-700 dark:text-zinc-300">Profile Banner</p>
                  <div className={`relative w-full h-24 rounded-xl shadow-inner overflow-hidden ${bannerClassName}`} style={bannerPreviewStyle}>
                    {banner && (
                      <button onClick={() => setBanner(null)} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition-colors">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <input ref={bannerInputRef} type="file" accept="image/*" onChange={handleBannerChange} className="hidden" />
                    <button onClick={() => bannerInputRef.current?.click()} className="px-4 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 rounded-xl hover:bg-slate-200 dark:hover:bg-zinc-700 transition-colors text-sm font-medium">Upload Image</button>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-slate-500 dark:text-zinc-400 mb-2">Preset gradients</p>
                    <div className="grid grid-cols-8 gap-1.5">
                      {[
                        'from-primary-500 via-primary-600 to-secondary-600',
                        'from-emerald-500 via-teal-500 to-cyan-500',
                        'from-orange-500 via-red-500 to-pink-500',
                        'from-violet-500 via-purple-500 to-indigo-500',
                        'from-slate-700 via-slate-800 to-slate-900',
                        'from-amber-400 via-orange-500 to-red-500',
                        'from-green-400 via-emerald-500 to-teal-500',
                        'from-blue-500 via-indigo-500 to-violet-500',
                      ].map((g) => (
                        <button key={g} onClick={() => setBanner(`gradient:${g}`)} className={`h-8 rounded-lg bg-gradient-to-br ${g} transition-all ${banner === `gradient:${g}` ? 'ring-2 ring-offset-1 ring-primary-500 scale-105' : 'hover:scale-105'}`} />
                      ))}
                    </div>
                  </div>

                  <div className="pt-2">
                    <p className="text-xs font-medium text-slate-500 dark:text-zinc-400 mb-2">Custom gradient</p>
                    <div
                      className="w-full h-12 rounded-lg mb-3"
                      style={{ background: `linear-gradient(${gradientAngle}deg, ${gradientColor1}, ${gradientColor2}, ${gradientColor3})` }}
                    />
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      {[
                        ['Start', gradientColor1, setGradientColor1],
                        ['Mid', gradientColor2, setGradientColor2],
                        ['End', gradientColor3, setGradientColor3],
                      ].map(([label, val, set]) => (
                        <div key={label}>
                          <p className="text-[10px] text-slate-500 dark:text-zinc-400 mb-1">{label}</p>
                          <input type="color" value={val} onChange={(e) => set(e.target.value)} className="w-full h-8 rounded-lg border border-slate-200 dark:border-zinc-700 cursor-pointer" />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <input type="range" min="-180" max="180" value={gradientAngle} onChange={(e) => setGradientAngle(parseInt(e.target.value))} className="flex-1 accent-primary-500" />
                      <span className="text-xs text-slate-500 dark:text-zinc-400 w-10 text-right">{gradientAngle}°</span>
                    </div>
                    <button
                      onClick={() => setBanner(`custom:${gradientAngle}:${gradientColor1}:${gradientColor2}:${gradientColor3}`)}
                      className="w-full py-2 rounded-xl text-sm font-medium bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700 transition-colors"
                    >Apply Custom Gradient</button>
                  </div>
                </div>
              </>
            )}

            {/* ── ABOUT ─────────────────────────────────────────── */}
            {activeTab === 'about' && (
              <>
                <div>
                  {fieldLabel('Core Expertise', '— a one-liner headline about what you do best')}
                  {textInput(coreExpertise, setCoreExpertise, 'e.g. "15 years designing mixed-use urban developments"')}
                </div>
                <div>
                  {fieldLabel('Bio')}
                  {textArea(bio, setBio, 'Share your background, what you work on, and what makes you unique...', 5)}
                  <p className="text-xs text-slate-400 dark:text-zinc-500 mt-1">{bio.length}/500 characters</p>
                </div>
                <div>
                  {fieldLabel('Ask Me About', '— what topics you love fielding questions on')}
                  {textArea(askMeAbout, setAskMeAbout, 'e.g. "Transit-oriented design, LEED certification process, managing large project teams..."', 3)}
                </div>
              </>
            )}

            {/* ── EXPERTISE ─────────────────────────────────────── */}
            {activeTab === 'expertise' && (
              <>
                <div>
                  {fieldLabel('Areas of Expertise', '— select from company-wide tags')}
                  {expertiseCategories.length === 0 ? (
                    <p className="text-sm text-slate-400 dark:text-zinc-500 italic py-3">No expertise tags configured yet. Ask an admin to add them in Settings.</p>
                  ) : (
                    <div className="space-y-4">
                      {expertiseCategories.map((cat) => (
                        <div key={cat.id}>
                          <p className="text-xs font-semibold text-slate-500 dark:text-zinc-400 uppercase tracking-wider mb-2">{cat.name}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {cat.tags.map((tag) => (
                              <button
                                key={tag}
                                type="button"
                                onClick={() => toggleExpertiseTag(tag)}
                                aria-pressed={areasOfExpertise.includes(tag)}
                                // No checkmark/width change on select — the fill flip
                                // alone signals state, so neighboring pills never shift.
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                  areasOfExpertise.includes(tag)
                                    ? 'bg-teal-500 text-white ring-2 ring-teal-300'
                                    : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:text-teal-700 dark:hover:text-teal-400'
                                }`}
                              >
                                {tag}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  {fieldLabel('Programs & Software Expertise', '— tools you use regularly')}
                  <TagInput
                    tags={programsExpertise}
                    onAdd={(t) => setProgramsExpertise((p) => [...p, t])}
                    onRemove={(t) => setProgramsExpertise((p) => p.filter((x) => x !== t))}
                    placeholder="Revit, AutoCAD, Rhino, SketchUp, Bluebeam..."
                    colorClass="bg-violet-50 text-violet-700 hover:bg-violet-100"
                  />
                </div>

                <div>
                  {fieldLabel('Skills')}
                  <TagInput
                    tags={skills}
                    onAdd={(t) => setSkills((p) => [...p, t])}
                    onRemove={(t) => setSkills((p) => p.filter((x) => x !== t))}
                    placeholder="Project Management, Sustainable Design, BIM..."
                    colorClass="bg-primary-50 text-primary-700 hover:bg-primary-100"
                  />
                </div>

                <div className="pt-2 border-t border-slate-100 dark:border-zinc-700">
                  <p className="text-sm font-semibold text-slate-700 dark:text-zinc-300 mb-3">Licenses & Certifications</p>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 dark:text-zinc-400">Name *</label>
                      <input type="text" value={newCert.name} onChange={(e) => setNewCert({ ...newCert, name: e.target.value })} placeholder="e.g., AIA, LEED AP" className={`mt-1 ${smallInputBase}`} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 dark:text-zinc-400">Issuing Organization</label>
                      <input type="text" value={newCert.issuer} onChange={(e) => setNewCert({ ...newCert, issuer: e.target.value })} placeholder="e.g., US Green Building Council" className={`mt-1 ${smallInputBase}`} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 dark:text-zinc-400">Issue Date</label>
                      <input type="month" value={newCert.date} onChange={(e) => setNewCert({ ...newCert, date: e.target.value })} className={`mt-1 ${smallInputBase}`} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 dark:text-zinc-400">Expiration Date</label>
                      <input type="month" value={newCert.expires} onChange={(e) => setNewCert({ ...newCert, expires: e.target.value })} className={`mt-1 ${smallInputBase}`} />
                    </div>
                  </div>
                  <button onClick={addCertification} disabled={!newCert.name.trim()} className="w-full py-2 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Add Certification</button>
                  <div className="space-y-2 mt-3">
                    {certifications.map((cert) => (
                      <div key={cert.id} className="flex items-start justify-between p-3 bg-slate-50 dark:bg-zinc-800/60 rounded-xl">
                        <div>
                          <p className="text-sm font-medium text-slate-800 dark:text-zinc-100">{cert.name}</p>
                          {cert.issuer && <p className="text-xs text-slate-500 dark:text-zinc-400">{cert.issuer}</p>}
                          <p className="text-xs text-slate-400 dark:text-zinc-500">{cert.date && `Issued ${cert.date}`}{cert.date && cert.expires && ' · '}{cert.expires && `Exp ${cert.expires}`}</p>
                        </div>
                        <button onClick={() => setCertifications((p) => p.filter((c) => c.id !== cert.id))} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-zinc-700 transition-colors">
                          <svg className="w-4 h-4 text-slate-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* ── BACKGROUND ────────────────────────────────────── */}
            {activeTab === 'background' && (
              <>
                <div>
                  {fieldLabel('Practice Area / Market')}
                  {textInput(practiceArea, setPracticeArea, 'e.g. Mixed-Use, Healthcare, Higher Education...')}
                </div>
                <div>
                  {fieldLabel('States I\'m Licensed In')}
                  <TagInput
                    tags={statesLicensed}
                    onAdd={(t) => { const upper = t.toUpperCase(); if (US_STATES.includes(upper) || t.length > 2) setStatesLicensed((p) => p.includes(t) ? p : [...p, t]); else if (t) setStatesLicensed((p) => p.includes(upper) ? p : [...p, upper]); }}
                    onRemove={(t) => setStatesLicensed((p) => p.filter((x) => x !== t))}
                    placeholder="TX, CA, NY... (type state abbrev or full name)"
                    colorClass="bg-blue-50 text-blue-700 hover:bg-blue-100"
                  />
                </div>
                {/* Bottom-align both inputs — the helper line under the High
                    School label would otherwise push its input lower than the
                    College input beside it. */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    {fieldLabel('High School Alma Mater')}
                    <p className="text-xs text-slate-400 dark:text-zinc-500 mb-1.5">Helpful for Education BD outreach</p>
                    <div className="mt-auto">
                      {textInput(highSchoolAlmaMater, setHighSchoolAlmaMater, 'School name')}
                    </div>
                  </div>
                  <div className="flex flex-col">
                    {fieldLabel('College / University')}
                    <div className="mt-auto">
                      {textInput(collegeAlmaMater, setCollegeAlmaMater, 'Institution name')}
                    </div>
                  </div>
                </div>
                <div>
                  {fieldLabel('I Volunteer At')}
                  {textInput(volunteerAt, setVolunteerAt, 'Organization or cause you support')}
                </div>
                <div>
                  {fieldLabel('Interests')}
                  <TagInput
                    tags={interests}
                    onAdd={(t) => setInterests((p) => [...p, t])}
                    onRemove={(t) => setInterests((p) => p.filter((x) => x !== t))}
                    placeholder="Photography, Hiking, Architecture Competitions..."
                    colorClass="bg-rose-50 text-rose-700 hover:bg-rose-100"
                  />
                </div>
              </>
            )}

            {/* ── PROJECTS ──────────────────────────────────────── */}
            {activeTab === 'projects' && (
              <>
                <div>
                  <p className="text-sm font-semibold text-slate-700 dark:text-zinc-300 mb-3">My MOREgroup Projects</p>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <input type="text" value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} placeholder="Project name *" className={`col-span-3 ${smallInputBase}`} />
                    <input type="text" value={newProject.role} onChange={(e) => setNewProject({ ...newProject, role: e.target.value })} placeholder="Your role" className={`col-span-2 ${smallInputBase}`} />
                    <input type="text" value={newProject.year} onChange={(e) => setNewProject({ ...newProject, year: e.target.value })} placeholder="Year" className={smallInputBase} />
                  </div>
                  <button onClick={addProject} disabled={!newProject.name.trim()} className="w-full py-2 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors mb-3">Add Project</button>
                  <p className="text-xs text-slate-400 dark:text-zinc-500 mb-3">VantagePoint integration coming soon — enter projects manually for now.</p>

                  <div className="space-y-2">
                    {moregroupProjects.map((proj) => (
                      <div key={proj.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zinc-800/60 rounded-xl">
                        <div>
                          <p className="text-sm font-medium text-slate-800 dark:text-zinc-100">{proj.name}</p>
                          <p className="text-xs text-slate-500 dark:text-zinc-400">{[proj.role, proj.year].filter(Boolean).join(' · ')}</p>
                        </div>
                        <button onClick={() => removeProject(proj.id)} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-zinc-700 transition-colors">
                          <svg className="w-4 h-4 text-slate-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    ))}
                    {moregroupProjects.length === 0 && <p className="text-sm text-slate-400 dark:text-zinc-500 text-center py-4">No projects added yet.</p>}
                  </div>
                </div>

                <div className="border-t border-slate-100 dark:border-zinc-700 pt-5">
                  {fieldLabel('Project Types Prior to MOREgroup', '— types of projects or sectors you worked on before joining')}
                  {textArea(priorProjects, setPriorProjects, 'e.g. K-12 schools in the Southeast, luxury residential, large-scale transit hubs...', 4)}
                </div>
              </>
            )}

            {/* ── OPEN TO ───────────────────────────────────────── */}
            {activeTab === 'opento' && (
              <>
                <div className="space-y-4">
                  {[
                    { label: 'I am open to Public Speaking', desc: 'Conferences, panels, webinars, and company presentations', val: openToSpeaking, set: setOpenToSpeaking },
                    { label: 'I am open to writing a White Paper', desc: 'Research publications, thought leadership articles, or case studies', val: openToWhitePaper, set: setOpenToWhitePaper },
                    { label: 'I am open to Mentoring', desc: 'Supporting colleagues through advice, guidance, and knowledge sharing', val: openToMentoring, set: setOpenToMentoring },
                  ].map(({ label, desc, val, set }) => (
                    <div key={label} className="flex items-start justify-between gap-4 p-4 bg-slate-50 dark:bg-zinc-800/60 rounded-xl">
                      <div>
                        <p className="text-sm font-medium text-slate-800 dark:text-zinc-100">{label}</p>
                        <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">{desc}</p>
                      </div>
                      <Toggle checked={val} onChange={set} label={label} />
                    </div>
                  ))}
                </div>

                {openToMentoring && (
                  <div>
                    {fieldLabel('I can mentor on these topics')}
                    {textArea(mentorTopics, setMentorTopics, 'e.g. Early career development, transitioning to project management, sustainable design...', 3)}
                  </div>
                )}

                <div className="border-t border-slate-100 dark:border-zinc-700 pt-5">
                  {fieldLabel('Innovative Ideas & Topics of Tomorrow I Enjoy Exploring')}
                  {textArea(innovationTopics, setInnovationTopics, 'What emerging ideas, technologies, or trends excite you? What do you enjoy thinking about and discussing?', 4)}
                </div>
              </>
            )}

          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 dark:border-zinc-700 flex justify-end gap-3 flex-shrink-0">
            <button onClick={onClose} className="px-4 py-2 text-slate-600 dark:text-zinc-400 hover:text-slate-800 dark:hover:text-zinc-100 hover:bg-slate-100 dark:hover:bg-zinc-800 rounded-xl transition-colors text-sm">Cancel</button>
            <button onClick={handleSave} disabled={isSaving} className="px-6 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm font-medium">
              {isSaving ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</>
              ) : 'Save Changes'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
